import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  FormControl,
  TextField,
  Typography,
  Alert,
  InputLabel,
  FormHelperText,
  Button,
  CircularProgress,
  Card,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { ADD_SUB_PACKAGE, ALL_SUB_LEVEL } from "../../gql/subscriptionPakcage";

const sectionCard = {
  borderRadius: "12px",
  border: "1px solid #eee",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  p: 3,
  mb: 2.5,
};

const CreateSubscriptionPackage = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState({ message: "", isError: false });
  const [values, setValues] = useState({});
  const [descriptionEN, setDescriptionEN] = useState([""]);
  const [descriptionMM, setDescriptionMM] = useState([""]);
  const [errors, setErrors] = useState({});
  const { data: subLevels } = useQuery(ALL_SUB_LEVEL);

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleChangeDescEN = (index) => (event) => {
    const newDescriptions = [...descriptionEN];
    newDescriptions[index] = event.target.value;
    setDescriptionEN(newDescriptions);
  };

  const handleChangeDescMM = (index) => (event) => {
    const newDescriptions = [...descriptionMM];
    newDescriptions[index] = event.target.value;
    setDescriptionMM(newDescriptions);
  };

  const addDescriptionFieldEN = () => setDescriptionEN([...descriptionEN, ""]);
  const removeDescriptionFieldEN = (index) => setDescriptionEN(descriptionEN.filter((_, i) => i !== index));
  const addDescriptionFieldMM = () => setDescriptionMM([...descriptionMM, ""]);
  const removeDescriptionFieldMM = (index) => setDescriptionMM(descriptionMM.filter((_, i) => i !== index));

  const [createSubPackage] = useMutation(ADD_SUB_PACKAGE, {
    onError: (error) => {
      console.log("error : ", error);
      alert("Sub Package Upload Error", error);
      setLoading(false);
    },
  });

  const handleCreate = async () => {
    setLoading(true);
    setErrors({});
    let isErrorExit = false;
    let errorObject = {};
    if (!values.title) { errorObject.title = "Title field is required."; isErrorExit = true; }
    if (isErrorExit) { setErrors({ ...errorObject }); setLoading(false); return; }
    try {
      await createSubPackage({
        variables: {
          addServicePackage: {
            title: values.title,
            description_en: ["test", "test"],
            description_mm: ["test", "test"],
            subscription_level_id: values.subscription_level_id,
          },
        },
      });
    } catch (error) {
      console.log("error : ", error);
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#fff",
          borderBottom: "1px solid #eee",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            Create Subscription Package
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Set up a new subscription package with features
          </Typography>
        </Box>
        <Button onClick={() => navigate("/sub_package")} variant="outlined" color="inherit" size="small" sx={{ borderColor: "#ddd", color: "#555" }}>
          Close
        </Button>
      </Box>

      <Box sx={{ maxWidth: 800, mx: "auto", px: 3, py: 3 }}>
        {/* Package Info */}
        <Card sx={sectionCard}>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>Package Info</Typography>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
            <TextField
              id="title"
              label="Title *"
              fullWidth
              value={values.title || ""}
              onChange={handleChange("title")}
              error={!!errors.title}
              helperText={errors.title}
            />
            <FormControl fullWidth>
              <InputLabel>Subscription Level</InputLabel>
              <Select
                label="Subscription Level"
                value={values.subscription_level_id || ""}
                onChange={handleChange("subscription_level_id")}
              >
                <MenuItem value="" disabled>Select level</MenuItem>
                {subLevels?.user_subscription_level?.map((level, index) => (
                  <MenuItem key={index} value={level.id}>{level.subscription_type}</MenuItem>
                ))}
              </Select>
              {errors.subscription_level_id && <FormHelperText error>{errors.subscription_level_id}</FormHelperText>}
            </FormControl>
          </Box>
        </Card>

        {/* Description EN */}
        <Card sx={sectionCard}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>Features (English)</Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={addDescriptionFieldEN}
              sx={{ color: "#ff5722", textTransform: "none" }}
            >
              Add Item
            </Button>
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {descriptionEN.map((desc, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Feature ${index + 1}`}
                  value={desc}
                  onChange={handleChangeDescEN(index)}
                  error={!!errors.description_en}
                  helperText={index === 0 ? errors.description_en : ""}
                />
                {descriptionEN.length > 1 && (
                  <IconButton onClick={() => removeDescriptionFieldEN(index)} size="small" sx={{ color: "#999" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Card>

        {/* Description MM */}
        <Card sx={sectionCard}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>Features (Myanmar)</Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={addDescriptionFieldMM}
              sx={{ color: "#ff5722", textTransform: "none" }}
            >
              Add Item
            </Button>
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {descriptionMM.map((desc, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Feature ${index + 1}`}
                  value={desc}
                  onChange={handleChangeDescMM(index)}
                  error={!!errors.description_mm}
                  helperText={index === 0 ? errors.description_mm : ""}
                />
                {descriptionMM.length > 1 && (
                  <IconButton onClick={() => removeDescriptionFieldMM(index)} size="small" sx={{ color: "#999" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Card>

        {/* Footer Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1, pb: 4 }}>
          <Button onClick={() => navigate("/sub_package")} variant="outlined" color="inherit" size="large" sx={{ borderColor: "#ddd", color: "#555", minWidth: 100 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={handleCreate}
            sx={{
              minWidth: 130,
              bgcolor: "#ff5722",
              "&:hover": { bgcolor: "#e64a19" },
              "&.Mui-disabled": { bgcolor: "#ffccbc", color: "#fff" },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
            {loading ? "Creating..." : "Create Package"}
          </Button>
        </Box>
      </Box>

      {showAlert.message && !showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="success">{showAlert.message}</Alert>
      )}
      {showAlert.message && showAlert.isError && (
        <Alert sx={{ position: "fixed", bottom: "1em", right: "1em" }} severity="error">{showAlert.message}</Alert>
      )}
    </Box>
  );
};

export default CreateSubscriptionPackage;
